import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Send, Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * ObfuscatedEmail — renders a live mailto link but the address itself is
 * never present in the initial HTML (so basic HTML scrapers miss it).
 * The pieces are joined on mount, which requires the scraper to actually
 * execute JS. Not bulletproof against headless-browser scrapers, but it
 * blocks the noisy 80% of naive harvesters for zero UX cost.
 */
export function ObfuscatedEmail({ user = "hello", domain = "peakmarketsociety.com", className = "" }) {
  const [address, setAddress] = useState("");
  useEffect(() => {
    // Delay one tick to keep the address out of the SSR/initial DOM snapshot.
    const id = setTimeout(() => setAddress(`${user}@${domain}`), 30);
    return () => clearTimeout(id);
  }, [user, domain]);
  if (!address) {
    return <span className={className} aria-hidden="true">{user}&#8203;<span style={{display:"none"}}>nospam</span>@&#8203;{domain}</span>;
  }
  return (
    <a
      href={`mailto:${address}`}
      className={className}
      data-testid="obfuscated-email"
    >
      {address}
    </a>
  );
}

/**
 * ContactDialog — modal contact form.
 * Anonymous submission → POST /api/contact. Includes:
 *   - honeypot field `website` (hidden with CSS)
 *   - render-timestamp `t` so backend can reject <3s submissions
 *   - name + email + subject + message
 * Shows success state after submit; auto-closes 2.5s later.
 */
export default function ContactDialog({ open, onClose, subject = "" }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", subject, message: "", website: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const renderedAtRef = useRef(0);

  useEffect(() => {
    if (open) {
      renderedAtRef.current = Date.now();
      setSent(false);
      setForm((f) => ({ ...f, subject: subject || f.subject }));
    }
  }, [open, subject]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (sending || sent) return;
    setSending(true);
    try {
      await api.post("/contact", {
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
        website: form.website, // honeypot — MUST stay ""
        t: renderedAtRef.current,
        lang: (typeof window !== "undefined" && localStorage.getItem("summit.lang")) || "en",
      });
      setSent(true);
      toast.success(t("contactDialogUi.messageSent"));
      setTimeout(() => { onClose?.(); }, 2400);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || t("contactDialogUi.sendError"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="contact-dialog-title"
      data-testid="contact-dialog"
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="relative w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden">
        <button
          type="button"
          onClick={onClose}
          aria-label={t("contactDialogUi.closeAriaLabel")}
          data-testid="contact-dialog-close"
          className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-900 hover:bg-slate-100 transition-colors"
        >
          <X size={16} />
        </button>

        <div className="bg-slate-950 text-white px-7 py-6">
          <div className="inline-flex items-center gap-2 rounded-full bg-amber-500/15 border border-amber-500/30 px-3 py-1 text-[10px] uppercase tracking-[0.28em] font-bold text-amber-300 mb-3">
            <Mail size={11} /> {t("contactDialogUi.reachHuman")}
          </div>
          <h2 id="contact-dialog-title" className="font-display font-black text-2xl tracking-tighter">
            {sent ? t("contactDialogUi.messageReceived") : t("contactDialogUi.howCanWeHelp")}
          </h2>
          <p className="mt-1 text-sm text-slate-300 leading-relaxed">
            {sent
              ? t("contactDialogUi.humanReplies")
              : t("contactDialogUi.typeQuestion")}
          </p>
        </div>

        {sent ? (
          <div className="px-7 py-10 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-emerald-100 text-emerald-700 mb-4">
              <Send size={22} />
            </div>
            <p className="text-slate-600 text-sm">
              {t("contactDialogUi.sentTo")} <span className="font-mono">hello@peakmarketsociety.com</span>. {t("contactDialogUi.watchInbox")}
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="px-7 py-6 space-y-4">
            {/* Honeypot — real users can't see this. Bots love filling it. */}
            <div style={{ position: "absolute", left: "-9999px", height: 0, overflow: "hidden" }} aria-hidden="true">
              <label htmlFor="contact-website">{t("contactDialogUi.websiteLabel")}</label>
              <input
                id="contact-website"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label htmlFor="contact-name" className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("contactDialogUi.yourName")}</Label>
                <Input
                  id="contact-name"
                  data-testid="contact-dialog-name"
                  required
                  maxLength={120}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1.5 h-11"
                />
              </div>
              <div>
                <Label htmlFor="contact-email" className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("contactDialogUi.replyToEmail")}</Label>
                <Input
                  id="contact-email"
                  data-testid="contact-dialog-email"
                  required
                  type="email"
                  maxLength={200}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1.5 h-11"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="contact-subject" className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("contactDialogUi.subject")}</Label>
              <Input
                id="contact-subject"
                data-testid="contact-dialog-subject"
                required
                maxLength={200}
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label htmlFor="contact-message" className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{t("contactDialogUi.message")}</Label>
              <textarea
                id="contact-message"
                data-testid="contact-dialog-message"
                required
                rows={5}
                maxLength={5000}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className="mt-1.5 w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-800 focus:border-transparent transition"
              />
              <div className="mt-1 text-[10px] text-slate-400 font-mono text-right">
                {form.message.length} / 5000
              </div>
            </div>

            <Button
              type="submit"
              data-testid="contact-dialog-submit"
              disabled={sending}
              className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-semibold"
            >
              {sending ? (
                <><Loader2 size={14} className="mr-2 animate-spin" /> {t("contactDialogUi.sending")}</>
              ) : (
                <><Send size={14} className="mr-2" /> {t("contactDialogUi.sendMessage")}</>
              )}
            </Button>

            <p className="text-[11px] text-slate-400 text-center leading-relaxed">
              {t("contactDialogUi.preferEmail")} <ObfuscatedEmail className="text-blue-800 underline underline-offset-2" />
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
